import { useI18n } from '@/i18n/I18nContext';
import { Input, Skeleton, Stack, Text, HStack, useDisclosure } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { DataTableColumnDef as ColumnDef, DataTableCellContext as CellContext, DataTableRow as Row } from 'src/components/DataTable';
import { Partner } from 'src/api/Partner';
import DataTable, { DataTableToolbar, dateCell } from 'src/components/DataTable';
import { partnerService } from 'src/service/PartnerService';
import CountryFlag from 'src/components/CountryFlag';
import { countries } from 'src/data/Countries';
import ConfirmDialog from 'src/components/ConfirmDialog';
import IdAndNameData from 'src/data/IdAndNameData';
import Content from 'src/components/Content';
import AddButton from 'src/components/buttons/AddButton';
import PartnersProps from './PartnersProps';
import WaitingSpinner from '@/components/WaitingSpinner';
import { DropDownMenu } from '@/components/DropDownMenu';
import { toaster } from '@/components/ui/toaster';
import ActiveOnlySwitch from '@/components/ActiveOnlySwitch';

const OverviewPartners = ({ consultant }: PartnersProps) => {
  const { t } = useI18n();

  const [data, setData] = useState<Partner[]>([]);
  const [activePartnerIds, setActivePartnerIds] = useState<Set<number>>(new Set());
  const [deleteData, setDeleteData] = useState<IdAndNameData<number> | null>(null);
  const [showActive, setShowActive] = useState(true);
  const [nameFilter, setNameFilter] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const { open: isOpen, onOpen, onClose } = useDisclosure();

  const navigate = useNavigate();

  const onDelete = useCallback((id: number, name: string): Promise<void> => {
    console.info(`deleting ${id} ${name}`);
    return partnerService.remove(id)
      .then(() => {
        setData(prev => prev.filter(item => item.id !== id));
        setDeleteData(null);
        onClose();
        toaster.create({
          title: consultant ? 'Konsulent gelöscht' : 'Partner gelöscht',
          type: 'success'
        });
      });
  }, [consultant, onClose]);

  const onEdit = useCallback((id: number): void => {
    console.info(`editing ${id}`);
    navigate(`/partners/edit/${id}`);
  }, [navigate]);

  const onView = useCallback((id: number): void => {
    console.info(`viewing ${id}`);
    navigate(`/partners/view/${id}`);
  }, [navigate]);

  const onCreateUser = useCallback((id: number): void => {
    console.info(`creating user for partner ${id}`);
    navigate(`/admin/users/add?partnerId=${id}`);
  }, [navigate]);

  useEffect(() => {
    setIsLoading(true);
    partnerService
      .listWithActivity(consultant)
      .then(r => {
        setData(r.results.concat());
        setActivePartnerIds(new Set(r.activePartnerIds));
      })
      .finally(() => setIsLoading(false));
  }, [consultant]);

  const onFilter = (row: Partner): boolean => {
    const matchesActive = !showActive || activePartnerIds.has(row.id);
    const check = nameFilter.trim().toLowerCase();
    const matchesName = check.length === 0 || row.name.trim().toLowerCase().includes(check);
    return matchesActive && matchesName;
  };

  const columns: ColumnDef<Partner>[] = useMemo(
    () => [
      {
        id: 'name',
        header: 'Name',
        accessorKey: 'name',
        cell: (info: CellContext<Partner, string>) => (<Text fontWeight={'bold'}>{info.getValue()}</Text>),
      },
      {
        id: 'country',
        header: 'Land',
        accessorKey: 'country',
        cell: (info: CellContext<Partner, string>) => (
          <HStack>
            <CountryFlag code={info.getValue()} />
            <Text>{countries[info.getValue()]}</Text>
          </HStack>
        ),
        sortFn: (rowA: Row<Partner>, rowB: Row<Partner>, _): number => {
          const a = rowA.original;
          const b = rowB.original;
          let ap = countries[a.country] ?? '';
          let bp = countries[b.country] ?? '';
          return ap.trim().localeCompare(bp.trim(), undefined, { sensitivity: 'base' });
        },
      },
      {
        id: 'currency',
        header: 'Währung',
        accessorFn: (row: Partner) => row.currency,
      },
      {
        id: 'bankName',
        header: 'Bank',
        accessorFn: (row: Partner) => row.bankName
      },
      {
        id: 'createdAt',
        header: () => (<div style={{ textAlign: 'right', width: '6rem' }}>Erstellt</div>),
        accessorFn: (row: Partner) => row.createdAt,
        cell: dateCell()
      },
      {
        id: 'edit',
        enableSorting: false,
        cell: info => {
          let row = info.row.original;
          return (
            <DropDownMenu onMenuView={() => onView(row.id)} onMenuEdit={() => onEdit(row.id)} onMenuCreateUser={!consultant ? () => onCreateUser(row.id) : undefined} onMenuDelete={() => {
              let da: IdAndNameData<number> = {
                id: row.id,
                name: row.name
              };
              setDeleteData(da);
              onOpen();
            }} />
          )
        }
      }
    ], [consultant, onCreateUser, onEdit, onOpen, onView]
  );

  return (
    <Content
      title={t(`overviews.${consultant ? 'consultants' : 'partners'}.title`)}
      description={t(`overviews.${consultant ? 'consultants' : 'partners'}.description`)}
      actions={<AddButton caption={t(`overviews.${consultant ? 'consultants' : 'partners'}.create`)} onClick={() => navigate(consultant ? '/partners/consultants/add' : '/partners/add')} />}
    >
      <Stack gap='6'>
        <DataTableToolbar resultCount={isLoading ? undefined : data.filter(onFilter).length}
          filtersActive={nameFilter.trim() !== '' || showActive} onClearFilters={() => { setNameFilter(''); setShowActive(false); }}>
          <Input aria-label={t(`overviews.${consultant ? 'consultants' : 'partners'}.search`)} placeholder={t(`overviews.${consultant ? 'consultants' : 'partners'}.search`)}
            w={{ base: 'full', md: '20rem' }} maxW='full' value={nameFilter} onChange={ev => setNameFilter(ev.currentTarget.value)} />
          <ActiveOnlySwitch checked={showActive} onChange={setShowActive} label={t('overviews.activeOnly')} />
        </DataTableToolbar>
        {isLoading ? (
          <Stack py={5}>
            <WaitingSpinner size='xl' />
            <Skeleton height='20px' />
            <Skeleton height='20px' />
            <Skeleton height='20px' />
          </Stack>
        ) : (
          <>
          <DataTable columns={columns} data={data} initialSortByColumn='createdAt' initialSortByDesc={true} hideColumnsOnXs={['currency', 'bankName', 'createdAt']}
            onRowClick={row => onView(row.id)}
            onFilter={onFilter} filter={`${showActive ? 'active' : 'all'}:${nameFilter.trim()}`}
          />
            <ConfirmDialog
              heading={`${deleteData?.name} Löschen`}
              isOpen={isOpen}
              onClose={onClose}
              onConfirm={async (): Promise<void> => onDelete(deleteData.id, deleteData.name)}
            />
          </>
        )}
      </Stack>
    </Content>
  );
}

export default OverviewPartners
